import React, { useContext, useState } from 'react';
import { WidgetPanelDetailProps } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanel';
import { selectEstimatedTradeCostWidgetsData } from 'widgets/estimatedTradeCost/store/data/dataSlice';
import {
    EstimatedTradeCostDataState,
    EstimatedTradeCostOrderDataState,
} from 'widgets/estimatedTradeCost/store/data/EstimateTradeCostDataState';
import { WidgetPanelGrid } from 'widgets/supportVisualisation/Content/Widgets/WidgetPanelGrid';
import { toUid } from 'widgets/estimatedTradeCost/store/data/EstimatedTradeCostRequest';
import { CheckboxInput } from '@instinet-analytics/semantic-ui-components';
import { NumberFormat } from '@instinet-analytics/core-lib';
import './WidgetPanelEstimatedTradeCost.scss';
import { ColDef } from 'ag-grid-community';
import { useAppSelector } from 'store/hooks';
import orderBy from 'lodash/orderBy';
import JsonPanel from 'utils/components/JsonPanel/JsonPanel';
import AppContext from 'app/AppContext';
import { StrikeBenchmark } from 'widgets/estimatedTradeCost/components/options';
import { strikeTimeToString } from 'widgets/estimatedTradeCost/store/data/mapping';

type ColumnItem = {
    key: string;
    orderId: string;
    benchmark: string;
    profile: string;
    minPartLit: number;
    maxPartLit: number;
    strikeTime: string;
    symbol: string;
    status: string;
    requestTime: Date;
    responseTime?: Date;
    cost?: string;
    risk?: string;
    data: EstimatedTradeCostOrderDataState;
};

export default function WidgetPanelEstimatedTradeCost(
    props: WidgetPanelDetailProps
) {
    const { state } = props;
    const data = selectEstimatedTradeCostWidgetsData(state.state);
    const boundData = useAppSelector(selectEstimatedTradeCostWidgetsData);
    const [useBound, setUseBound] = useState(false);
    const [selectedRow, setSelectedRow] = useState<
        EstimatedTradeCostOrderDataState | undefined
    >(undefined);
    const { numberFormat } = useContext(AppContext);

    const onToggleBound = () => {
        setUseBound(!useBound);
    };
    const flattened = flattenData(useBound ? boundData : data, numberFormat);

    const onSelectedRowIdsChanged = (rowIds: string[]) => {
        if (rowIds.length !== 1) {
            return;
        }

        const row = flattened.find((f) => f.key === rowIds[0]);
        setSelectedRow(row?.data);
    };

    return (
        <div className="widget-panel WidgetPanelEstimatedTradeCost">
            <CheckboxInput
                className="bind-to-current"
                label={'Bind to current'}
                checked={useBound}
                onToggle={onToggleBound}
                tooltip={
                    'Bind to the current application state rather than the state loaded into Support Visualisation'
                }
            />
            <WidgetPanelGrid
                data={flattened}
                getRowDataId={(r) => r.key}
                csvFileName={'estTradeCost.csv'}
                colDefs={getColumns()}
                onSelectedRowIdsChanged={onSelectedRowIdsChanged}
            />
            {selectedRow && <JsonPanel data={selectedRow} />}
        </div>
    );
}

export function addEstimatedTradeCostPanes(
    detailProps: WidgetPanelDetailProps,
    panes: { menuItem: string; render: () => JSX.Element }[]
) {
    const render = () => <WidgetPanelEstimatedTradeCost {...detailProps} />;
    const pane = {
        menuItem: 'Data',
        render: render,
    };
    panes.splice(0, 0, pane);
}

function flattenData(
    data: EstimatedTradeCostDataState,
    numberFormat: NumberFormat
): ColumnItem[] {
    return orderBy(
        Object.values(data).map((i) => flattenItem(i, numberFormat)),
        ['requestTime'],
        ['desc']
    );
}

function flattenItem(
    data: EstimatedTradeCostOrderDataState,
    numberFormat: NumberFormat
): ColumnItem {
    return {
        key: toUid(data.request),
        orderId: data.request.orderId,
        benchmark: data.request.benchmark,
        profile: data.request.profile,
        symbol: data.request.inetSymbol,
        minPartLit: data.request.minPartLit,
        maxPartLit: data.request.maxPartLit,
        strikeTime:
            data.request.benchmark === StrikeBenchmark &&
            data.request.strikeTime
                ? strikeTimeToString(data.request.strikeTime)
                : 'n/a',
        status: data.status,
        requestTime: new Date(data.requestTimestamp),
        data,
        responseTime: data.responseTimestamp
            ? new Date(data.responseTimestamp)
            : undefined,
        risk: numberFormat.n2(
            data.response?.results
                ? data.response.results[0]?.summary.current_risk
                : undefined
        ),
        cost: numberFormat.n2(
            data.response?.results
                ? data.response.results[0]?.summary.bm_cost
                : undefined
        ),
    };
}

function getColumns(): ColDef[] {
    const toCol = (field: string, width = 80) => ({
        field,
        key: field,
        width,
        resizable: true,
    });

    return [
        toCol('orderId', 90),
        toCol('benchmark', 80),
        toCol('profile', 110),
        toCol('minPartLit', 80),
        toCol('maxPartLit', 80),
        toCol('strikeTime', 80),
        toCol('symbol'),
        toCol('status', 60),
        toCol('requestTime', 110),
        toCol('responseTime', 110),
        toCol('risk', 50),
        toCol('cost', 50),
    ];
}
